import type { FC } from 'react'

import { config } from '@skytech/config'
import { Input, Select, Toggle } from '@skytech/ui'
import { Button } from '@skytech/ui/Button'
import { useQuery } from '@tanstack/react-query'
import GlobeIcon from 'mdi-react/GlobeIcon'
import TrayArrowDownIcon from 'mdi-react/TrayArrowDownIcon'

import { Translation } from '@/app/translations/Translation'
import { useTranslation } from '@/app/translations/translations-hooks'
import queries from '@/modules/admin/queries'

import type { ReportType } from '../resources'
import adminResources from '../resources'

import { useReportsContext } from './index.context'
import { yearOptions } from './utils'

import type { ReportFilterOptions } from '.'

export const ReportFilters: FC<{
  filters: Omit<ReportFilterOptions, 'selectedReportType'> & {
    selectedReportType: ReportType | undefined
  }
  isReportListLoading: boolean
  onSetQueryFilters: (
    filters: Omit<ReportFilterOptions, 'selectedReportType'> & {
      selectedReportType: ReportType | undefined
    }
  ) => void
  onOpen: () => void
}> = ({ filters, isReportListLoading, onSetQueryFilters, onOpen }) => {
  const { count } = useReportsContext()
  const translate = useTranslation()
  const isDevelopmentMode = config.ENVIRONMENT === 'dev'

  const { data: customers } = useQuery(
    queries.adminReportCustomers(),
    adminResources.getReportCustomers
  )

  // TODO: enable for production
  const reportTypes: {
    value: ReportType
    label: string
  }[] = filterItems([
    isDevelopmentMode && { value: 'consumption', label: translate('Consumption (usage)') },
    { value: 'salary-deduction-usage', label: translate('Salary Deduction Usage') },
    { value: 'accounting', label: translate('Accounting Report') },
    isDevelopmentMode && { value: 'tax-advantage', label: translate('Tax Advantage Report') },
    { value: 'ssd-order', label: translate('Salary Deduction Device') },
    isDevelopmentMode && { value: 'off-boarding', label: translate('Off Boarding Report') },
    { value: 'new-number-orders', label: translate('New Number Orders') },
  ])

  return (
    <div className="items:start mb-5 flex w-full flex-col md:flex-row md:items-end">
      <div className="w-56 md:w-80">
        <Select
          id="select-report-type"
          value={filters.selectedReportType}
          placeholder={translate('Select report type')}
          label={<Translation id="Select report type" />}
          options={reportTypes}
          onChange={option => {
            const selectedReportType = option?.value
            if (!selectedReportType) throw Error('No report type has been selected!')
            onSetQueryFilters({ ...filters, selectedReportType: selectedReportType })
          }}
        />
      </div>

      <div className="ml-0 mr-4 md:mx-4 md:mr-0">
        <Select
          id="select-customer"
          value={filters.selectedCustomer}
          placeholder="Select customer"
          label={<Translation id="Select customer" />}
          options={customers?.map(cus => ({ label: cus.name, value: cus.id })) || []}
          onChange={option =>
            onSetQueryFilters({
              ...filters,
              selectedCustomer: option?.value ? Number(option?.value) : undefined,
              isGlobal: false,
            })
          }
          isClearable={true}
        />
      </div>

      {filters.selectedReportType === 'tax-advantage' ? (
        <div className="mx-0 w-56 md:mx-4">
          <Select
            id="select-year"
            value={filters.year}
            placeholder="Select year"
            label={<Translation id="Select year" />}
            options={yearOptions()}
            onChange={option =>
              onSetQueryFilters({ ...filters, year: option ? Number(option?.value) : '' })
            }
          />
        </div>
      ) : (
        <>
          <div className="ml-0 mr-4 md:mx-4 md:mr-0">
            <Input
              required
              capitalizeLabel
              isFullWidth
              id="fromDate"
              type="date"
              value={filters.fromDate}
              ariaLabel={translate('From Date')}
              label={<Translation id="From Date" />}
              onChange={e => onSetQueryFilters({ ...filters, fromDate: e.target.value })}
            />
          </div>
          <div className="ml-0 mr-4 md:mx-4 md:mr-0">
            <Input
              required
              capitalizeLabel
              isFullWidth
              id="toDate"
              type="date"
              value={filters.toDate}
              ariaLabel={translate('To Date')}
              label={<Translation id="To Date" />}
              onChange={e => onSetQueryFilters({ ...filters, toDate: e.target.value })}
            />
          </div>
        </>
      )}

      <div className="mx-4 w-40">
        <Toggle
          id="is-global"
          label={
            <div className="flex items-center gap-1">
              <Translation id="Global" />
              <GlobeIcon className="h-4 w-4" />
            </div>
          }
          checked={filters.isGlobal}
          marginBottom={16}
          marginTop={16}
          onChange={() =>
            onSetQueryFilters({
              ...filters,
              isGlobal: !filters.isGlobal,
              selectedCustomer: !filters.isGlobal ? undefined : filters.selectedCustomer,
            })
          }
        />
      </div>

      {count > 0 && (
        <div className="pt-4 text-right md:ml-auto md:w-56 md:pt-0">
          <Button
            Icon={<TrayArrowDownIcon />}
            disabled={isReportListLoading}
            variant="primary"
            onClick={onOpen}
            className="m-0 h-12"
          >
            <Translation id="Download Report" />
          </Button>
        </div>
      )}
    </div>
  )
}

const filterItems = <T,>(items: (T | false | null | undefined)[]) => {
  const filteredItems: T[] = []
  items.forEach(item => {
    if (!item) return
    filteredItems.push(item)
  })

  return filteredItems
}
